import React, { useMemo, useState } from "react";
import { fondueData } from "../../utils/fondueData";
import { usePanier } from "../../contexts/PanierContext";

/**
 * ============================================================
 * ICÔNE — FONDUE
 * Affiche une icône SVG de caquelon utilisée sur les cartes,
 * les titres et les éléments de réassurance.
 * ============================================================
 */
function FondueIcon({ className = "fondue-svg" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <path d="M18 29h28v8c0 8-6 14-14 14s-14-6-14-14v-8Z" />
      <path d="M14 29h36" />
      <path d="M22 51h20" />
      <path d="M25 18c-3-4 3-6 0-10" />
      <path d="M33 18c-3-4 3-6 0-10" />
      <path d="M41 18c-3-4 3-6 0-10" />
    </svg>
  );
}

/**
 * ============================================================
 * ICÔNE — COMPOSITION LIBRE
 * Affiche une icône SVG de fromage utilisée pour l’entrée
 * “ma propre composition”.
 * ============================================================
 */
function CheeseIcon({ className = "fondue-svg" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <path d="M10 31 32 18l22 13v18H10V31Z" />
      <path d="M10 31h44" />
      <circle cx="24" cy="38" r="2" />
      <circle cx="38" cy="40" r="2" />
      <circle cx="31" cy="45" r="1.5" />
    </svg>
  );
}

/**
 * ============================================================
 * ICÔNE — QUALITÉ
 * Réutilise l’icône fromage pour le bloc de réassurance qualité.
 * ============================================================
 */
function QualityIcon() {
  return <CheeseIcon className="fondue-mini-svg" />;
}

/**
 * ============================================================
 * ICÔNE — ARTISANAT
 * Affiche une icône SVG liée à la préparation artisanale.
 * ============================================================
 */
function CraftIcon() {
  return (
    <svg viewBox="0 0 64 64" className="fondue-mini-svg" aria-hidden="true">
      <path d="M18 42h28" />
      <path d="M22 42c0-9 5-15 10-15s10 6 10 15" />
      <path d="M28 25a4 4 0 1 1 8 0" />
      <path d="M16 48h32" />
    </svg>
  );
}

/**
 * ============================================================
 * ICÔNE — RETRAIT
 * Réutilise l’icône fondue pour indiquer la disponibilité
 * sur place / retrait.
 * ============================================================
 */
function PickupIcon() {
  return <FondueIcon className="fondue-mini-svg" />;
}

/**
 * ============================================================
 * CONSTANTES — FILTRES DES FONDUES TRADITIONNELLES
 * Définit les filtres régionaux affichés au-dessus des recettes.
 * Les labels sont résolus via getFilterLabel pour être traduisibles.
 * ============================================================
 */
const FILTERS = [
  { id: "all", fallback: "Toutes", translationKey: "fondueFilterAll" },
  {
    id: "romande",
    fallback: "Romandes",
    translationKey: "fondueFilterRomande",
  },
  {
    id: "alemanique",
    fallback: "Alémaniques",
    translationKey: "fondueFilterAlemanique",
  },
  { id: "tessin", fallback: "Tessin", translationKey: "fondueFilterTessin" },
];

/**
 * ============================================================
 * CONSTANTES — CATÉGORIES PRODUITS POUR COMPOSITION LIBRE
 * Liste les catégories de produits autorisées dans la fondue libre.
 * ============================================================
 */
const CUSTOM_FONDUE_CATEGORIES = [
  "Chèvre & Brebis",
  "Fromage Persillé - Bleu -",
  "Fromage Vache Pâte dur & mi-dur",
  "Fromage Vache Pâte Molle",
];

/**
 * ============================================================
 * CONSTANTES — LABELS CATÉGORIES COMPOSITION LIBRE
 * Définit les boutons de catégories, avec clés de traduction.
 * ============================================================
 */
const CUSTOM_CATEGORY_LABELS = [
  {
    id: "chevre-brebis",
    fallback: "Chèvre & Brebis",
    translationKey: "fondueCategoryGoatSheep",
  },
  { id: "bleu", fallback: "Bleus", translationKey: "fondueCategoryBlue" },
  {
    id: "dur-mi-dur",
    fallback: "Durs & mi-durs",
    translationKey: "fondueCategoryHard",
  },
  {
    id: "molle",
    fallback: "Pâtes molles",
    translationKey: "fondueCategorySoft",
  },
];

/**
 * ============================================================
 * CONSTANTES — LIMITES ET CALCULS
 * Définit les règles métier de la composition libre.
 * ============================================================
 */
const MAX_CUSTOM_CHEESES = 3;
const MIN_CUSTOM_PEOPLE = 5;

/**
 * ============================================================
 * CONSTANTES — PRÉSETS DE RATIOS
 * Définit les répartitions proposées selon le nombre de fromages.
 * ============================================================
 */
const CUSTOM_RATIO_PRESETS = {
  1: [[100]],
  2: [
    [50, 50],
    [25, 75],
    [75, 25],
  ],
  3: [
    [30, 30, 40],
    [30, 40, 30],
    [40, 30, 30],
  ],
};

/**
 * ============================================================
 * CONSTANTES — IMAGE ET TARIFICATION
 * Centralise l’image fondue et les bases de calcul quantité/prix.
 * ============================================================
 */
const FONDUE_IMAGE_URL =
  "https://app.marchedeluxe.ch/images/fondue-hero-main_2.png";

const GRAMS_PER_PERSON = 200;
const PRICE_PER_PERSON_CENTS = 950;

/**
 * ============================================================
 * HELPER — TRADUCTION SIMPLE
 * Retourne une traduction si disponible, sinon un fallback.
 * ============================================================
 */
function tr(t, key, fallback) {
  return t?.[key] || fallback;
}

/**
 * ============================================================
 * HELPER — TRADUCTION DYNAMIQUE
 * Résout une clé stockée dans les données, sinon retourne le texte brut.
 * ============================================================
 */
function translateDynamic(t, key, fallback) {
  if (key && t?.[key]) return t[key];
  return fallback || "";
}

/**
 * ============================================================
 * HELPER — LABEL FILTRE
 * Traduit le label des filtres régionaux.
 * ============================================================
 */
function getFilterLabel(t, filter) {
  return tr(t, filter.translationKey, filter.fallback);
}

/**
 * ============================================================
 * HELPER — LABEL CATÉGORIE
 * Traduit le label des catégories de fromages.
 * ============================================================
 */
function getCustomCategoryLabel(t, category) {
  return tr(t, category.translationKey, category.fallback);
}

/**
 * ============================================================
 * HELPER — GROUPE RÉGIONAL
 * Classe une fondue traditionnelle dans un groupe régional.
 * ============================================================
 */
function getFondueGroup(item) {
  const region = String(item?.region || "").toLowerCase();

  if (region.includes("tessin")) return "tessin";

  if (
    region.includes("fribourg") ||
    region.includes("neuchâtel") ||
    region.includes("neuchatel") ||
    region.includes("valais") ||
    region.includes("vaud") ||
    region.includes("jura")
  ) {
    return "romande";
  }

  return "alemanique";
}

/**
 * ============================================================
 * HELPER — BADGE FONDUE
 * Retourne le texte du badge affiché sur une recette.
 * ============================================================
 */
function getFondueFlag(item) {
  return item?.badgeLabel || "CH";
}

/**
 * ============================================================
 * HELPER — COULEUR BADGE
 * Retourne la couleur du badge d’une recette.
 * ============================================================
 */
function getFondueBadgeColor(item) {
  return item?.badgeColor || "#b08a45";
}

/**
 * ============================================================
 * HELPER — FORMAT NOM FONDUE
 * Normalise l’affichage du nom de la fondue.
 * ============================================================
 */
function formatFondueName(name) {
  const raw = String(name || "").trim();
  if (!raw) return "";

  return raw.charAt(0) + raw.slice(1).toLowerCase();
}

/**
 * ============================================================
 * HELPER — FORMAT PRIX
 * Convertit des centimes en prix CHF lisible.
 * ============================================================
 */
function formatCHF(cents) {
  const value = Number(cents || 0) / 100;
  return `CHF ${value.toFixed(2)}`;
}

/**
 * ============================================================
 * HELPER — CONVERSION CENTIMES
 * Normalise différents formats de prix en centimes.
 * ============================================================
 */
function toCents(value) {
  if (value === null || value === undefined || value === "") return 0;

  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;

  if (Number.isInteger(n) && n >= 100) return Math.round(n);

  return Math.round(n * 100);
}

/**
 * ============================================================
 * HELPER — NORMALISATION TEXTE
 * Rend un texte comparable en minuscules sans accents.
 * ============================================================
 */
function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * ============================================================
 * HELPER — NOM PRODUIT
 * Extrait le nom d’un produit fromage.
 * ============================================================
 */
function getProductName(product) {
  return String(product?.name || product?.nom || product?.title || "").trim();
}

/**
 * ============================================================
 * HELPER — UNITÉ PRODUIT
 * Extrait l’unité de vente d’un produit fromage.
 * ============================================================
 */
function getProductUnit(product) {
  return String(
    product?.unitLabel ||
      product?.unite ||
      product?.unit ||
      product?.unité ||
      ""
  ).trim();
}

/**
 * ============================================================
 * HELPER — PRIX PRODUIT
 * Extrait le prix exploitable d’un produit fromage.
 * ============================================================
 */
function getProductPriceCents(product) {
  return (
    toCents(product?.displayPrice) ||
    toCents(product?.appliedPrice) ||
    toCents(product?.price) ||
    toCents(product?.prix) ||
    0
  );
}

/**
 * ============================================================
 * HELPER — CATÉGORIE PRODUIT
 * Extrait la catégorie d’un produit fromage.
 * ============================================================
 */
function getProductCategory(product) {
  return String(
    product?.category ||
      product?.categorie ||
      product?.type ||
      product?.rayon ||
      product?.famille ||
      ""
  ).trim();
}

/**
 * ============================================================
 * HELPER — CATÉGORIE AUTORISÉE
 * Vérifie si un produit appartient aux catégories fondue libre.
 * ============================================================
 */
function isAllowedFondueCategory(product) {
  const category = normalizeText(getProductCategory(product));

  return CUSTOM_FONDUE_CATEGORIES.some((allowed) =>
    category.includes(normalizeText(allowed))
  );
}

/**
 * ============================================================
 * HELPER — GROUPE CATÉGORIE LIBRE
 * Transforme la catégorie produit en groupe utilisé par les filtres.
 * ============================================================
 */
function getCustomCategoryGroup(product) {
  const category = normalizeText(getProductCategory(product));

  if (category.includes("chevre") || category.includes("brebis")) {
    return "chevre-brebis";
  }

  if (category.includes("persille") || category.includes("bleu")) {
    return "bleu";
  }

  if (category.includes("dur") || category.includes("mi-dur")) {
    return "dur-mi-dur";
  }

  if (category.includes("molle")) {
    return "molle";
  }

  return "all";
}

/**
 * ============================================================
 * HELPER — PRODUIT FROMAGE
 * Vérifie qu’un produit est utilisable dans la composition libre.
 * ============================================================
 */
function isCheeseProduct(product) {
  if (!product || !product.id) return false;

  if (product.__isCave === true || product.product_scope === "cave") {
    return false;
  }

  return isAllowedFondueCategory(product);
}

/**
 * ============================================================
 * HELPER — MODE DE VENTE
 * Détermine si le produit est vendu au poids ou à la pièce.
 * ============================================================
 */
function getProductSaleMode(product) {
  const unit = normalizeText(getProductUnit(product));

  if (
    unit.includes("100g") ||
    unit.includes("100 g") ||
    unit.includes("/100") ||
    unit.includes("poids") ||
    unit === "g" ||
    unit === "kg"
  ) {
    return "weight";
  }

  return "piece";
}

/**
 * ============================================================
 * HELPER — CLÉ LIGNE CUSTOM
 * Retourne une clé stable pour chaque produit sélectionné.
 * ============================================================
 */
function getCustomLineKey(product) {
  return String(product?.id || "");
}

/**
 * ============================================================
 * HELPER — DÉTAILS PANIER CUSTOM
 * Construit le libellé détaillé affiché dans le panier.
 * ============================================================
 */
function buildCustomDetailsLabel(customItems, peopleCount, totalGrams) {
  const lines = customItems.map((line) => {
    const percent = Number(line.percent || 0);
    const grams = Math.round((totalGrams * percent) / 100);

    if (line.saleMode === "piece") {
      return `${line.name} ${percent}% (≈ ${grams}g souhaités, pièce à poids variable)`;
    }

    return `${line.name} ${percent}% (${grams}g)`;
  });

  return `Fondue personnalisée — ${totalGrams}g pour ${peopleCount} personnes — ${lines.join(
    ", "
  )}`;
}

/**
 * ============================================================
 * HELPER — ITEM PANIER CUSTOM
 * Construit l’objet panier pour une fondue personnalisée.
 * ============================================================
 */
function buildCustomFondueCartItem(
  customItems,
  peopleCount,
  totalGrams,
  totalPriceCents
) {
  const composition = customItems.map((line) => {
    const percent = Number(line.percent || 0);
    const grams = Math.round((totalGrams * percent) / 100);

    return {
      productId: line.productId,
      name: line.name,
      category: line.category,
      saleMode: line.saleMode,
      percent,
      grams,
      unitLabel: line.saleMode === "weight" ? "100g" : "pièce",
      unitPriceCents: line.unitPriceCents,
      note:
        line.saleMode === "piece"
          ? "Pièce à poids variable, grammes indicatifs pour le mélange."
          : "",
    };
  });

  return {
    id: `fondue-personnalisee-${Date.now()}`,
    name: "Fondue personnalisée",
    nom: "Fondue personnalisée",
    title: "Fondue personnalisée",

    typeProduit: "fondue-composee",
    item_type: "fondue-personnalisee",
    isFondue: true,

    qty: 1,
    quantity: 1,
    quantite: 1,
    grams: 0,

    price: totalPriceCents,
    displayPrice: totalPriceCents,
    appliedPrice: totalPriceCents,
    basePrice: totalPriceCents,
    publicPrice: totalPriceCents,

    image: FONDUE_IMAGE_URL,
    imageUrl: FONDUE_IMAGE_URL,
    image_url: FONDUE_IMAGE_URL,

    details_label: buildCustomDetailsLabel(
      customItems,
      peopleCount,
      totalGrams
    ),

    fondueConfig: {
      mode: "personnalisee",
      name: "Fondue personnalisée",
      peopleCount,
      gramsPerPerson: GRAMS_PER_PERSON,
      totalGrams,
      ratioTotal: 100,
      composition,
    },
  };
}

/**
 * ============================================================
 * HELPER — LIBELLÉ PANIER TRADITIONNEL
 * Construit le détail panier d’une recette traditionnelle.
 * ============================================================
 */
function buildFondueDetailsLabel(fondue, peopleCount, totalGrams) {
  const ratios = Array.isArray(fondue?.ratios) ? fondue.ratios : [];

  const composition = ratios
    .map((ratio) => {
      const percent = Number(ratio.percent || 0);
      const grams = Math.round((totalGrams * percent) / 100);
      return `${ratio.cheese} ${percent}% (${grams}g)`;
    })
    .join(", ");

  return `${formatFondueName(
    fondue?.name
  )} — ${totalGrams}g pour ${peopleCount} personnes — ${composition}`;
}

/**
 * ============================================================
 * HELPER — ITEM PANIER TRADITIONNEL
 * Construit l’objet panier pour une fondue traditionnelle.
 * ============================================================
 */
function buildFondueCartItem(fondue, peopleCount, totalGrams, totalPriceCents) {
  const ratios = Array.isArray(fondue?.ratios) ? fondue.ratios : [];

  const computedRatios = ratios.map((ratio) => {
    const percent = Number(ratio.percent || 0);

    return {
      cheese: ratio.cheese,
      percent,
      grams: Math.round((totalGrams * percent) / 100),
    };
  });

  const fondueName = formatFondueName(fondue?.name);

  return {
    id: `fondue-${fondue?.id}`,
    name: fondueName,
    nom: fondueName,
    title: fondueName,

    typeProduit: "fondue-composee",
    item_type: "fondue-traditionnelle",
    isFondue: true,

    qty: 1,
    quantity: 1,
    quantite: 1,
    grams: 0,

    price: totalPriceCents,
    displayPrice: totalPriceCents,
    appliedPrice: totalPriceCents,
    basePrice: totalPriceCents,
    publicPrice: totalPriceCents,

    image: FONDUE_IMAGE_URL,
    imageUrl: FONDUE_IMAGE_URL,
    image_url: FONDUE_IMAGE_URL,

    details_label: buildFondueDetailsLabel(fondue, peopleCount, totalGrams),

    fondueConfig: {
      mode: "traditionnelle",
      recipeId: fondue?.id,
      name: fondueName,
      region: fondue?.region,
      peopleCount,
      gramsPerPerson: GRAMS_PER_PERSON,
      totalGrams,
      ratios: computedRatios,
    },
  };
}

/**
 * ============================================================
 * PAGE — COMPOSITION DE FONDUE
 * Gère l’écran d’entrée, les fondues traditionnelles, le détail
 * recette et la composition personnalisée.
 * ============================================================
 */
export default function FondueComposerView({
  t,
  setVueActuelle,
  produits = [],
}) {
  const { ajouterAuPanier } = usePanier();

  /**
   * ------------------------------------------------------------
   * ÉTATS — NAVIGATION INTERNE
   * Contrôle l’écran affiché dans le tunnel fondue.
   * ------------------------------------------------------------
   */
  const [screen, setScreen] = useState("entry");

  /**
   * ------------------------------------------------------------
   * ÉTATS — FONDUE TRADITIONNELLE
   * Stocke le filtre actif, la recette sélectionnée et le nombre
   * de personnes.
   * ------------------------------------------------------------
   */
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedFondue, setSelectedFondue] = useState(null);
  const [peopleCount, setPeopleCount] = useState(4);

  /**
   * ------------------------------------------------------------
   * ÉTATS — FEEDBACK
   * Affiche les messages courts après validation ou erreur.
   * ------------------------------------------------------------
   */
  const [addedFeedback, setAddedFeedback] = useState("");

  /**
   * ------------------------------------------------------------
   * ÉTATS — COMPOSITION LIBRE
   * Stocke les fromages sélectionnés, la catégorie, la recherche,
   * le nombre de personnes et l’étape active.
   * ------------------------------------------------------------
   */
  const [customItems, setCustomItems] = useState([]);
  const [customCategory, setCustomCategory] = useState("all");
  const [customSearch, setCustomSearch] = useState("");
  const [customPeopleCount, setCustomPeopleCount] = useState(MIN_CUSTOM_PEOPLE);
  const [customStep, setCustomStep] = useState("cheeses");

  /**
   * ------------------------------------------------------------
   * MÉMO — FONDUES FILTRÉES
   * Filtre les recettes traditionnelles selon la région active.
   * ------------------------------------------------------------
   */
  const filteredFondues = useMemo(() => {
    if (activeFilter === "all") return fondueData;
    return fondueData.filter((item) => getFondueGroup(item) === activeFilter);
  }, [activeFilter]);

  /**
   * ------------------------------------------------------------
   * MÉMO — PRODUITS FROMAGE
   * Filtre les produits disponibles pour la composition libre.
   * ------------------------------------------------------------
   */
  const cheeseProducts = useMemo(() => {
    const search = normalizeText(customSearch);

    return (Array.isArray(produits) ? produits : [])
      .filter((product) => isCheeseProduct(product))
      .filter((product) => getProductPriceCents(product) > 0)
      .filter((product) => {
        if (customCategory === "all") return false;
        return getCustomCategoryGroup(product) === customCategory;
      })
      .filter((product) => {
        if (!search) return true;
        return normalizeText(getProductName(product)).includes(search);
      })
      .slice(0, 80);
  }, [produits, customCategory, customSearch]);

  /**
   * ------------------------------------------------------------
   * CALCULS — COMPOSITION LIBRE
   * Calcule poids total, total des ratios, validité et prix.
   * ------------------------------------------------------------
   */
  const customTotalGrams = customPeopleCount * GRAMS_PER_PERSON;

  const customRatioTotal = useMemo(() => {
    return customItems.reduce(
      (sum, line) => sum + Number(line.percent || 0),
      0
    );
  }, [customItems]);

  const customRatioIsValid =
    customItems.length > 0 && Number(customRatioTotal) === 100;

  const customTotalPriceCents = useMemo(() => {
    return customItems.reduce((sum, line) => {
      const grams = Math.round(
        (customTotalGrams * Number(line.percent || 0)) / 100
      );

      if (line.saleMode === "weight") {
        return sum + Math.round((line.unitPriceCents * grams) / 100);
      }

      return sum + Math.max(1, Math.ceil(grams / 100)) * line.unitPriceCents;
    }, 0);
  }, [customItems, customTotalGrams]);

  const customTotalWeightLabel = useMemo(() => {
    if (!customItems.length) return "0 g";

    return `${customTotalGrams} g pour ${customPeopleCount} personnes`;
  }, [customItems.length, customTotalGrams, customPeopleCount]);

  /**
   * ------------------------------------------------------------
   * CALCULS — FONDUE TRADITIONNELLE
   * Calcule poids total et prix selon le nombre de personnes.
   * ------------------------------------------------------------
   */
  const totalGrams = peopleCount * GRAMS_PER_PERSON;
  const totalPriceCents = peopleCount * PRICE_PER_PERSON_CENTS;

  /**
   * ------------------------------------------------------------
   * ACTION — RETOUR GLOBAL
   * Gère le retour depuis la page fondue vers l’accueil.
   * Utilise "accueil" pour éviter la page blanche liée à "home".
   * ------------------------------------------------------------
   */
  const goBackHome = () => {
    if (screen === "detail") {
      setScreen("traditional");
      return;
    }

    if (screen === "custom") {
      setScreen("entry");
      return;
    }

    if (screen !== "entry") {
      setScreen("entry");
      return;
    }

    if (typeof setVueActuelle === "function") {
      setVueActuelle("accueil");
    }
  };

  /**
   * ------------------------------------------------------------
   * ACTION — OUVERTURE COMPOSITION LIBRE
   * Affiche l’écran de création personnalisée.
   * ------------------------------------------------------------
   */
  const openCustomFondue = () => {
    setAddedFeedback("");
    setScreen("custom");
  };

  /**
   * ------------------------------------------------------------
   * ACTION — OUVERTURE DÉTAIL RECETTE
   * Sélectionne une recette traditionnelle et affiche son détail.
   * ------------------------------------------------------------
   */
  const openFondueDetail = (fondue) => {
    setSelectedFondue(fondue);
    setPeopleCount(4);
    setAddedFeedback("");
    setScreen("detail");
  };

  /**
   * ------------------------------------------------------------
   * ACTION — PERSONNES TRADITIONNEL
   * Diminue ou augmente le nombre de personnes.
   * ------------------------------------------------------------
   */
  const decreasePeople = () => {
    setPeopleCount((current) => Math.max(2, current - 1));
  };

  const increasePeople = () => {
    setPeopleCount((current) => Math.min(12, current + 1));
  };

  /**
   * ------------------------------------------------------------
   * ACTION — AJOUT FROMAGE CUSTOM
   * Ajoute un fromage dans le caquelon personnalisé et redistribue
   * automatiquement les ratios.
   * ------------------------------------------------------------
   */
  const addCustomProduct = (product) => {
    const key = getCustomLineKey(product);
    const saleMode = getProductSaleMode(product);
    const name = getProductName(product);
    const unitPriceCents = getProductPriceCents(product);

    if (!key || !name || unitPriceCents <= 0) return;

    setCustomItems((prev) => {
      const alreadyExists = prev.some((line) => line.key === key);
      if (alreadyExists) return prev;

      if (prev.length >= MAX_CUSTOM_CHEESES) {
        setAddedFeedback(
          tr(t, "fondueMaxThreeCheeses", "Maximum 3 fromages par caquelon")
        );

        window.setTimeout(() => {
          setAddedFeedback("");
        }, 1800);

        return prev;
      }

      const nextCount = prev.length + 1;
      const defaultPercent = Math.floor(100 / nextCount);
      const remainder = 100 - defaultPercent * nextCount;

      const redistributed = prev.map((line, index) => ({
        ...line,
        percent: defaultPercent + (index === 0 ? remainder : 0),
      }));

      return [
        ...redistributed,
        {
          key,
          productId: product.id,
          name,
          saleMode,
          percent: defaultPercent,
          unitPriceCents,
          category: getProductCategory(product),
        },
      ];
    });
  };

  /**
   * ------------------------------------------------------------
   * ACTION — SUPPRESSION FROMAGE CUSTOM
   * Retire un fromage du caquelon personnalisé.
   * ------------------------------------------------------------
   */
  const removeCustomProduct = (key) => {
    setCustomItems((prev) => {
      const next = prev.filter((line) => line.key !== key);

      if (next.length < 3) {
        setCustomStep("cheeses");
      }

      return next;
    });
  };

  /**
   * ------------------------------------------------------------
   * ACTION — VALIDATION FROMAGES CUSTOM
   * Passe à l’étape de sélection des ratios après choix fromage.
   * ------------------------------------------------------------
   */
  const validateCustomCheeses = () => {
    if (customItems.length === 0) {
      setAddedFeedback(
        tr(t, "fondueAddOneCheese", "Ajoutez au moins un fromage au caquelon")
      );
      window.setTimeout(() => setAddedFeedback(""), 1800);
      return;
    }

    setCustomStep("ratios");

    window.setTimeout(() => {
      const ratioBlock = document.querySelector(".fondue-ratio-presets");
      ratioBlock?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  /**
   * ------------------------------------------------------------
   * ACTION — APPLICATION PRÉSET
   * Applique une répartition de ratios prédéfinie.
   * ------------------------------------------------------------
   */
  const applyCustomRatioPreset = (preset) => {
    setCustomItems((prev) =>
      prev.map((line, index) => ({
        ...line,
        percent: Number(preset[index] || 0),
      }))
    );
  };

  /**
   * ------------------------------------------------------------
   * ACTION — PERSONNES CUSTOM
   * Diminue ou augmente le nombre de personnes de la fondue libre.
   * ------------------------------------------------------------
   */
  const decreaseCustomPeople = () => {
    setCustomPeopleCount((current) => Math.max(MIN_CUSTOM_PEOPLE, current - 1));
  };

  const increaseCustomPeople = () => {
    setCustomPeopleCount((current) => Math.min(12, current + 1));
  };

  /**
   * ------------------------------------------------------------
   * ACTION — AJOUT PANIER TRADITIONNEL
   * Crée l’objet panier d’une fondue traditionnelle et l’ajoute.
   * ------------------------------------------------------------
   */
  const handleAddFondueToCart = () => {
    if (!selectedFondue || typeof ajouterAuPanier !== "function") return;

    const fondueItem = buildFondueCartItem(
      selectedFondue,
      peopleCount,
      totalGrams,
      totalPriceCents
    );

    ajouterAuPanier(fondueItem, 1, 0);
    setAddedFeedback(
      tr(t, "fondueTraditionalAddedToCart", "Fondue ajoutée au panier")
    );

    window.setTimeout(() => {
      setAddedFeedback("");
    }, 1800);
  };

  /**
   * ------------------------------------------------------------
   * ACTION — AJOUT PANIER CUSTOM
   * Vérifie les ratios puis ajoute la fondue personnalisée au panier.
   * ------------------------------------------------------------
   */
  const handleAddCustomFondueToCart = () => {
    if (!customItems.length || typeof ajouterAuPanier !== "function") return;

    if (!customRatioIsValid) {
      setAddedFeedback(
        tr(
          t,
          "fondueRatioMustBe100",
          "Le total des ratios doit être égal à 100%"
        )
      );

      window.setTimeout(() => {
        setAddedFeedback("");
      }, 1800);

      return;
    }

    const customFondueItem = buildCustomFondueCartItem(
      customItems,
      customPeopleCount,
      customTotalGrams,
      customTotalPriceCents
    );

    ajouterAuPanier(customFondueItem, 1, 0);
    setAddedFeedback(
      tr(t, "fondueCustomAddedToCart", "Fondue personnalisée ajoutée au panier")
    );

    window.setTimeout(() => {
      setAddedFeedback("");
    }, 1800);
  };

  return (
    <main
      className={`fondue-composer-view page-transition fondue-screen-${screen}`}
      style={{
        position: "relative",
        isolation: "isolate",
        minHeight: "100dvh",
        width: "100%",
      }}
    >
      {/* Arrière-plan global fixe : couvre tout l’écran, y compris tablette/desktop et la zone sous la nav-bar. */}
      <div
        className="fondue-page-background"
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* Écran d’entrée : choix entre fondue traditionnelle et composition libre. */}
      {screen === "entry" && (
        <>
          {/* Barre de retour vers l’accueil. */}
          <div className="fondue-topbar">
            <button
              type="button"
              className="fondue-back-btn"
              onClick={goBackHome}
            >
              ← {tr(t, "fondueBack", "Retour")}
            </button>
          </div>

          {/* Hero principal de la page fondue. */}
          {/* Cartes d’entrée : sélection du parcours utilisateur */}
          <section className="fondue-entry-cards">
            <button
              type="button"
              className="fondue-entry-card"
              onClick={() => setScreen("traditional")}
            >
              <span className="fondue-card-icon">
                <FondueIcon />
              </span>

              <span className="fondue-card-text">
                <strong>
                  {tr(t, "fondueTraditionalTitle", "FONDUES TRADITIONNELLES")}
                </strong>
                <small>
                  {tr(
                    t,
                    "fondueTraditionalSubtitle",
                    "Découvrez nos recettes cantonales"
                  )}
                </small>
              </span>

              <span className="fondue-card-arrow">›</span>
            </button>

            <button
              type="button"
              className="fondue-entry-card"
              onClick={openCustomFondue}
            >
              <span className="fondue-card-icon">
                <CheeseIcon />
              </span>

              <span className="fondue-card-text">
                <strong>
                  {tr(t, "fondueCustomTitle", "MA PROPRE COMPOSITION")}
                </strong>
                <small>
                  {tr(
                    t,
                    "fondueCustomSubtitle",
                    "Choisissez vos fromages et composez votre mélange"
                  )}
                </small>
              </span>

              <span className="fondue-card-arrow">›</span>
            </button>
          </section>

          {/* Hero déplacé en dessous */}
          <section className="fondue-hero">
            <div className="fondue-hero-copy">
              <div className="fondue-brand-mark">
                {tr(t, "fondueBrand", "STERCHI")}
              </div>

              <h1>{tr(t, "fondueHeroTitle", "COMPOSEZ VOTRE FONDUE")}</h1>

              <div className="fondue-separator">
                <span />
              </div>

              <p>
                {tr(
                  t,
                  "fondueHeroText",
                  "Découvrez nos recettes traditionnelles suisses ou créez votre propre mélange selon vos goûts."
                )}
              </p>
            </div>
          </section>
          {/* Bloc de réassurance : qualité, artisanat et retrait. */}
          <section className="fondue-reassurance">
            <div>
              <QualityIcon />
              <strong>{tr(t, "fondueQualityTitle", "Qualité premium")}</strong>
              <small>{tr(t, "fondueQualityText", "Fromages AOP")}</small>
            </div>

            <div>
              <CraftIcon />
              <strong>{tr(t, "fondueCraftTitle", "Fait avec passion")}</strong>
              <small>{tr(t, "fondueCraftText", "Maison Sterchi")}</small>
            </div>

            <div>
              <PickupIcon />
              <strong>{tr(t, "fonduePickupTitle", "Sur place")}</strong>
              <small>
                {tr(t, "fonduePickupText", "Prête à votre arrivée")}
              </small>
            </div>
          </section>
        </>
      )}

      {/* Écran liste : affiche les recettes traditionnelles filtrables. */}
      {screen === "traditional" && (
        <section className="fondue-list-screen">
          {/* Barre supérieure de la liste traditionnelle. */}
          <div className="fondue-list-topbar">
            <button
              type="button"
              className="fondue-list-back"
              onClick={() => setScreen("entry")}
            >
              ←
            </button>

            <div className="fondue-list-title">
              {tr(t, "fondueTraditionalTitle", "FONDUES TRADITIONNELLES")}
            </div>
          </div>

          {/* Introduction de la liste des fondues traditionnelles. */}
          <div className="fondue-list-intro">
            <div className="fondue-list-icon">
              <FondueIcon />
            </div>

            <div className="fondue-list-intro-copy">
              <h2>
                {tr(t, "fondueTraditionalListTitle", "Nos fondues cantonales")}
              </h2>
              <p>
                {tr(
                  t,
                  "fondueTraditionalListText",
                  "Des recettes authentiques inspirées des traditions suisses."
                )}
              </p>
            </div>
          </div>

          {/* Filtres régionaux des fondues traditionnelles. */}
          <div className="fondue-filter-row">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={
                  activeFilter === filter.id
                    ? "fondue-filter active"
                    : "fondue-filter"
                }
                onClick={() => setActiveFilter(filter.id)}
              >
                {getFilterLabel(t, filter)}
              </button>
            ))}
          </div>

          {/* Liste des recettes correspondant au filtre actif. */}
          <div className="fondue-list">
            {filteredFondues.map((fondue) => (
              <button
                key={fondue.id}
                type="button"
                className="fondue-list-card"
                onClick={() => openFondueDetail(fondue)}
              >
                <div className="fondue-list-image">
                  <img src={FONDUE_IMAGE_URL} alt="" aria-hidden="true" />

                  <span
                    className="fondue-flag"
                    style={{ background: getFondueBadgeColor(fondue) }}
                  >
                    {getFondueFlag(fondue)}
                  </span>
                </div>

                <div className="fondue-list-copy">
                  <strong>
                    {formatFondueName(fondue.name)}
                    <br />
                    <span>
                      ({translateDynamic(t, fondue.regionKey, fondue.region)})
                    </span>
                  </strong>

                  <small>
                    {(Array.isArray(fondue.cheeses) ? fondue.cheeses : []).join(
                      " · "
                    )}
                  </small>
                </div>

                <span className="fondue-list-arrow">›</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Écran détail : affiche la composition et l’ajout panier d’une recette traditionnelle. */}
      {screen === "detail" && selectedFondue && (
        <section className="fondue-detail-screen">
          {/* Barre supérieure du détail recette. */}
          <div className="fondue-detail-topbar">
            <button
              type="button"
              className="fondue-detail-back"
              onClick={() => setScreen("traditional")}
            >
              ←
            </button>

            <div className="fondue-detail-title">
              {formatFondueName(selectedFondue.name)}
            </div>

            <button
              type="button"
              className="fondue-detail-heart"
              aria-label={tr(t, "fondueFavorite", "Favori")}
            >
              ♡
            </button>
          </div>

          {/* Image principale de la recette sélectionnée. */}
          <div className="fondue-detail-image">
            <img src={FONDUE_IMAGE_URL} alt="" aria-hidden="true" />

            <span
              className="fondue-detail-badge"
              style={{ background: getFondueBadgeColor(selectedFondue) }}
            >
              {getFondueFlag(selectedFondue)}
            </span>
          </div>

          {/* Contenu détaillé de la recette. */}
          <div className="fondue-detail-content">
            {/* Présentation de la fondue sélectionnée. */}
            <section className="fondue-detail-intro-card">
              <h2>{formatFondueName(selectedFondue.name)}</h2>
              <p>
                {translateDynamic(
                  t,
                  selectedFondue.descriptionKey,
                  selectedFondue.description
                )}
              </p>
            </section>

            {/* Composition traditionnelle avec ratios et grammes calculés. */}
            <section className="fondue-detail-panel">
              <h3>
                {tr(
                  t,
                  "fondueTraditionalComposition",
                  "COMPOSITION TRADITIONNELLE"
                )}
              </h3>

              {selectedFondue.ratios.map((ratio) => {
                const grams = Math.round(
                  (totalGrams * Number(ratio.percent || 0)) / 100
                );

                return (
                  <div key={ratio.cheese} className="fondue-ratio-block">
                    <div className="fondue-ratio-head">
                      <span>{ratio.cheese} </span>
                      <strong>
                        {ratio.percent}% · {grams}g
                      </strong>
                    </div>

                    <div className="fondue-ratio-track">
                      <span style={{ width: `${ratio.percent}%` }} />
                    </div>
                  </div>
                );
              })}
            </section>

            {/* Sélecteur du nombre de personnes et quantité calculée. */}
            <section className="fondue-detail-panel">
              <h3>{tr(t, "fondueTotalQuantity", "QUANTITÉ TOTALE")}</h3>

              <div className="fondue-quantity-row">
                <span>{tr(t, "fonduePeopleCount", "Nombre de personnes")}</span>

                <div className="fondue-people-control">
                  <button type="button" onClick={decreasePeople}>
                    −
                  </button>
                  <strong>{peopleCount}</strong>
                  <button type="button" onClick={increasePeople}>
                    +
                  </button>
                </div>
              </div>

              <div className="fondue-quantity-row">
                <span>
                  {tr(t, "fondueRecommendedQuantity", "Quantité recommandée")}
                </span>
                <strong>{totalGrams} g</strong>
              </div>
            </section>

            {/* Message temporaire d’ajout panier ou d’erreur. */}
            {addedFeedback && (
              <div className="fondue-added-feedback">{addedFeedback}</div>
            )}
          </div>

          {/* Barre panier fixe en bas pour la fondue traditionnelle. */}
          <div className="fondue-sticky-cart">
            <div>
              <strong>{totalGrams} g</strong>
              <small>
                {tr(t, "fondueForPeople", "Pour")} {peopleCount}{" "}
                {tr(t, "fonduePeople", "personnes")}
              </small>
            </div>

            <button type="button" onClick={handleAddFondueToCart}>
              <span>{tr(t, "fondueAddToCart", "AJOUTER AU PANIER")}</span>
              <small>{formatCHF(totalPriceCents)}</small>
            </button>
          </div>
        </section>
      )}

      {/* Écran custom : permet de composer une fondue personnalisée. */}
      {screen === "custom" && (
        <section className="fondue-custom-screen">
          {/* Barre supérieure de la composition personnalisée. */}
          <div className="fondue-detail-topbar">
            <button
              type="button"
              className="fondue-detail-back"
              onClick={() => setScreen("entry")}
            >
              ←
            </button>

            <div className="fondue-detail-title">
              {tr(t, "fondueCustomTitle", "MA COMPOSITION")}
            </div>

            <span />
          </div>

          {/* Introduction de la composition personnalisée. */}
          <div className="fondue-custom-intro">
            <div className="fondue-list-icon">
              <CheeseIcon />
            </div>

            <div className="fondue-list-intro-copy">
              <h2>
                {tr(t, "fondueCustomIntroTitle", "Composez votre caquelon")}
              </h2>
              <p>
                {tr(
                  t,
                  "fondueCustomIntroText",
                  "Sélectionnez jusqu’à 3 fromages, puis choisissez le ratio de votre mélange. Le total doit faire 100%."
                )}
              </p>
            </div>
          </div>

          {/* Layout principal de la composition libre. */}
          <div className="fondue-custom-layout">
            {/* Panneau de réglage : personnes, quantité, catégories et recherche. */}
            <section className="fondue-custom-panel">
              <div className="fondue-quantity-row custom-people-row">
                <span>{tr(t, "fonduePeopleCount", "Nombre de personnes")}</span>

                <div className="fondue-people-control">
                  <button type="button" onClick={decreaseCustomPeople}>
                    −
                  </button>
                  <strong>{customPeopleCount}</strong>
                  <button type="button" onClick={increaseCustomPeople}>
                    +
                  </button>
                </div>
              </div>

              <div className="fondue-quantity-row">
                <span>
                  {tr(t, "fondueRecommendedQuantity", "Quantité recommandée")}
                </span>
                <strong>{customTotalGrams} g</strong>
              </div>

              <h3>{tr(t, "fondueCategories", "CATÉGORIES")}</h3>

              <div className="fondue-custom-categories">
                {CUSTOM_CATEGORY_LABELS.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={
                      customCategory === category.id
                        ? "fondue-custom-category active"
                        : "fondue-custom-category"
                    }
                    onClick={() => setCustomCategory(category.id)}
                  >
                    {getCustomCategoryLabel(t, category)}
                  </button>
                ))}
              </div>

              <input
                className="fondue-custom-search"
                type="search"
                value={customSearch}
                placeholder={tr(
                  t,
                  "fondueSearchPlaceholder",
                  "Rechercher un fromage..."
                )}
                onChange={(e) => setCustomSearch(e.target.value)}
              />
            </section>

            {/* Panneau produits : affiche les fromages disponibles selon la catégorie. */}
            {customStep === "cheeses" &&
            customItems.length < MAX_CUSTOM_CHEESES &&
            customCategory !== "all" ? (
              <section className="fondue-custom-panel">
                <h3>
                  {tr(t, "fondueAvailableCheeses", "FROMAGES DISPONIBLES")}
                </h3>

                {cheeseProducts.length === 0 ? (
                  <div className="fondue-custom-empty">
                    {tr(
                      t,
                      "fondueNoCheeseInCategory",
                      "Aucun fromage disponible dans cette catégorie."
                    )}
                  </div>
                ) : (
                  <div className="fondue-custom-products-list">
                    {cheeseProducts.map((product) => {
                      const alreadySelected = customItems.some(
                        (line) => line.key === getCustomLineKey(product)
                      );

                      const disabled =
                        alreadySelected ||
                        customItems.length >= MAX_CUSTOM_CHEESES;

                      return (
                        <button
                          key={product.id}
                          type="button"
                          className={
                            alreadySelected
                              ? "fondue-custom-product selected"
                              : "fondue-custom-product"
                          }
                          onClick={() => addCustomProduct(product)}
                          disabled={disabled}
                        >
                          <span>
                            <strong>{getProductName(product)}</strong>
                            <small>{getProductCategory(product)}</small>
                          </span>

                          <em>
                            {alreadySelected
                              ? tr(t, "fondueInCaquelon", "Au caquelon")
                              : tr(
                                  t,
                                  "fondueAddToCaquelon",
                                  "Ajouter au caquelon"
                                )}
                          </em>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>
            ) : customStep === "cheeses" ? (
              <div className="fondue-custom-empty">
                {tr(
                  t,
                  "fondueChooseCategoryHint",
                  "Sélectionnez une catégorie pour voir les fromages disponibles."
                )}
              </div>
            ) : null}

            {/* Panneau mélange : liste les fromages sélectionnés et gère les ratios. */}
            <section className="fondue-custom-panel">
              <h3>{tr(t, "fondueMyMix", "MON MÉLANGE")}</h3>

              {/* État vide du mélange personnalisé. */}
              {customItems.length === 0 ? (
                <p className="fondue-custom-placeholder">
                  {tr(
                    t,
                    "fondueMixEmpty",
                    "Ajoutez un fromage au caquelon pour commencer votre mélange."
                  )}
                </p>
              ) : (
                <div className="fondue-custom-lines">
                  {customItems.map((line) => {
                    const grams = Math.round(
                      (customTotalGrams * Number(line.percent || 0)) / 100
                    );

                    return (
                      <div key={line.key} className="fondue-custom-line">
                        <div className="fondue-custom-line-main">
                          <strong>{line.name}</strong>
                          <small>
                            {line.percent}% ·{" "}
                            {line.saleMode === "piece"
                              ? `≈ ${grams}g souhaités · pièce variable`
                              : `${grams}g`}
                          </small>
                        </div>

                        <button
                          type="button"
                          className="fondue-custom-remove"
                          onClick={() => removeCustomProduct(line.key)}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Étape ratios : propose des répartitions visuelles selon le nombre de fromages. */}
              {customItems.length > 0 && customStep === "ratios" && (
                <div className="fondue-ratio-presets">
                  <div className="fondue-ratio-header">
                    <span className="fondue-ratio-mini-icon">
                      <FondueIcon />
                    </span>

                    <h3>
                      {tr(
                        t,
                        "fondueRatioTitle",
                        "CONCOCTEZ VOTRE FONDUE IDÉALE"
                      )}
                    </h3>
                    <strong>
                      {tr(t, "fondueRatioSubtitle", "SÉLECTIONNEZ VOS RATIOS")}
                    </strong>
                    <small>
                      {customItems.length}{" "}
                      {customItems.length > 1
                        ? tr(
                            t,
                            "fondueSelectedCheesePlural",
                            "fromages sélectionnés"
                          )
                        : tr(
                            t,
                            "fondueSelectedCheeseSingular",
                            "fromage sélectionné"
                          )}
                    </small>
                  </div>

                  <div className="fondue-ratio-card-grid">
                    {(CUSTOM_RATIO_PRESETS[customItems.length] || []).map(
                      (preset) => {
                        const isActive = customItems.every(
                          (line, index) =>
                            Number(line.percent) === Number(preset[index])
                        );

                        return (
                          <button
                            key={preset.join("-")}
                            type="button"
                            onClick={() => applyCustomRatioPreset(preset)}
                            className={
                              isActive
                                ? "fondue-ratio-visual-card active"
                                : "fondue-ratio-visual-card"
                            }
                          >
                            <span className="fondue-ratio-check">
                              {isActive ? "✓" : ""}
                            </span>

                            <div className="fondue-ratio-composition-row">
                              {preset.map((value, index) => (
                                <div
                                  key={`${value}-${index}`}
                                  className="fondue-ratio-composition-item"
                                >
                                  <strong>{value}%</strong>
                                  <small>{customItems[index]?.name}</small>
                                </div>
                              ))}
                            </div>
                          </button>
                        );
                      }
                    )}
                  </div>

                  <div className="fondue-ratio-tip">
                    {tr(
                      t,
                      "fondueRatioTip",
                      "Conseil : plus la part d’un fromage est élevée, plus son goût sera présent dans votre fondue."
                    )}
                  </div>
                </div>
              )}

              {/* Bouton de validation du caquelon avant l’étape ratios. */}
              {customItems.length > 0 && customStep === "cheeses" && (
                <button
                  type="button"
                  className="fondue-validate-caquelon"
                  onClick={validateCustomCheeses}
                >
                  {tr(t, "fondueValidateCaquelon", "Valider mon caquelon")}
                </button>
              )}

              {/* Total des ratios : indique si le mélange atteint bien 100%. */}
              {customStep === "ratios" && (
                <div
                  className={
                    customRatioIsValid
                      ? "fondue-ratio-total valid"
                      : "fondue-ratio-total"
                  }
                >
                  <span>{tr(t, "fondueRatioTotal", "Total ratios")}</span>
                  <strong>{customRatioTotal}%</strong>
                </div>
              )}

              {/* Message temporaire d’ajout panier ou d’erreur custom. */}
              {addedFeedback && (
                <div className="fondue-added-feedback">{addedFeedback}</div>
              )}
            </section>
          </div>

          {/* Barre panier fixe en bas pour la composition personnalisée. */}
          <div className="fondue-sticky-cart">
            <div>
              <strong>{formatCHF(customTotalPriceCents)}</strong>
              <small>{customTotalWeightLabel}</small>
            </div>

            <button
              type="button"
              onClick={handleAddCustomFondueToCart}
              disabled={
                customStep !== "ratios" ||
                !customItems.length ||
                !customRatioIsValid
              }
            >
              <span>{tr(t, "fondueAddToCart", "AJOUTER AU PANIER")}</span>
              <small>
                {customStep !== "ratios"
                  ? tr(
                      t,
                      "fondueValidateCaquelonFirst",
                      "Validez le caquelon d’abord"
                    )
                  : customRatioIsValid
                  ? tr(t, "fondueCustomAddedToCart", "Fondue personnalisée")
                  : tr(t, "fondueRatioRequired", "Ratio à 100% requis")}
              </small>
            </button>
          </div>
        </section>
      )}

      <style jsx="true">{`
        .fondue-composer-view {
          min-height: 100vh;
          background: transparent;
          color: #241c17;
          padding-bottom: 0;
          font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
          overflow-x: hidden;
          position: relative;
        }
        .fondue-topbar,
        .fondue-hero,
        .fondue-entry-cards,
        .fondue-reassurance,
        .fondue-list-screen,
        .fondue-detail-screen,
        .fondue-custom-screen {
          position: relative;
          z-index: 1;
        }

        .fondue-page-background {
          background-color: #fbf8f1;
          background-size: cover;
          background-position: center top;
          background-repeat: no-repeat;
        }

        /* Écran d’entrée fondue : image fondue sous la navbar */
        .fondue-screen-entry .fondue-page-background {
          background-image: linear-gradient(
              180deg,
              rgba(251, 248, 241, 0.72) 0%,
              rgba(251, 248, 241, 0.48) 26%,
              rgba(251, 248, 241, 0.18) 52%,
              rgba(36, 28, 23, 0.18) 76%,
              rgba(251, 248, 241, 0.58) 100%
            ),
            url("https://app.marchedeluxe.ch/images/fondue-hero-main_2.png");
        }

        .fondue-screen-traditional .fondue-page-background {
          background-image: linear-gradient(
              180deg,
              rgba(251, 248, 241, 0.95) 0%,
              rgba(251, 248, 241, 0.85) 15%,
              rgba(251, 248, 241, 0.55) 35%,
              rgba(251, 248, 241, 0.15) 65%,
              rgba(251, 248, 241, 0.05) 100%
            ),
            url("https://app.marchedeluxe.ch/images/ArrierePlanPageTradi.png");
        }

        .fondue-screen-custom .fondue-page-background {
          background-image: linear-gradient(
              180deg,
              rgba(251, 248, 241, 0.25) 0%,
              rgba(251, 248, 241, 0.15) 100%
            ),
            url("https://app.marchedeluxe.ch/images/ArrierPlanPagePerso.png");
        }

        .fondue-topbar {
          width: min(100%, 920px);
          margin: 0 auto;
          padding: 0 22px;
          position: relative;
          z-index: 5;
        }

        .fondue-back-btn {
          border: none;
          background: transparent;
          color: #241c17;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: pointer;
          padding: 8px 0 4px;
        }

        .fondue-hero {
          width: 100%;
          min-height: auto;
          margin: 20px 0 0;
          padding: 40px 22px 40px;
          position: relative;
          overflow: hidden;
          border-radius: 0;
          background: transparent;
        }

        .fondue-hero-copy {
          position: relative;
          z-index: 1;
          max-width: 430px;
          padding-top: 4px;
        }

        .fondue-brand-mark {
          font-family: "Playfair Display", serif;
          color: #b08a45;
          font-size: 1.38rem;
          letter-spacing: 0.18em;
          font-weight: 600;
          margin-bottom: 14px;
          text-align: center;
        }

        .fondue-hero h1 {
          font-family: "Playfair Display", serif;
          font-size: clamp(2.65rem, 10.5vw, 4.15rem);
          line-height: 0.96;
          font-weight: 700;
          margin: 0;
          letter-spacing: -0.035em;
          color: #241c17;
        }

        .fondue-separator {
          margin: 20px 0 18px;
          width: 100px;
          height: 1px;
          background: #b08a45;
          position: relative;
        }

        .fondue-separator span {
          width: 9px;
          height: 9px;
          background: #b08a45;
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%) rotate(45deg);
        }

        .fondue-hero p {
          font-size: 1rem;
          line-height: 1.58;
          margin: 0;
          max-width: 330px;
          color: rgba(28, 22, 18, 0.96);
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.55);
        }

        .fondue-entry-cards {
          width: min(78%, 520px);
          margin: 24px auto 0;
          padding: 0;
          position: relative;
          z-index: 3;
          display: grid;
          gap: 14px;
        }

        .fondue-entry-card {
          min-height: 94px;
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 20px;

          /* 👇 beaucoup plus transparent */
          background: rgba(255, 250, 242, 0.05);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(6px);

          /* 👇 ombre beaucoup plus douce */
          box-shadow: 0 1px 0 rgba(255, 255, 255, 0.25) inset,
            0 6px 18px rgba(0, 0, 0, 0.08);

          display: grid;
          grid-template-columns: 60px 1fr 42px;
          align-items: center;
          gap: 10px;
          padding: 14px 18px;
          text-align: left;
          cursor: pointer;
        }

        .fondue-svg,
        .fondue-mini-svg {
          fill: none;
          stroke: #b08a45;
          stroke-linecap: round;
          stroke-linejoin: round;
          filter: none;
        }

        .fondue-svg {
          width: 34px;
          height: 34px;
          stroke-width: 3;
        }

        .fondue-mini-svg {
          width: 30px;
          height: 30px;
          stroke-width: 2.6;
        }

        .fondue-card-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .fondue-card-text {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .fondue-card-text strong {
          font-size: 0.92rem;
          font-weight: 800;
          letter-spacing: 0.075em;
          line-height: 1.25;
        }

        .fondue-card-text small {
          font-size: 0.78rem;
          line-height: 1.35;
          color: rgba(36, 28, 23, 0.64);
        }

        .fondue-card-arrow,
        .fondue-list-arrow {
          width: 38px;
          height: 38px;
          border-radius: 999px;
          color: #b08a45;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.9rem;
          line-height: 1;
        }

        .fondue-card-arrow {
          background: rgba(176, 138, 69, 0.88);
          color: #fff;
          box-shadow: 0 8px 18px rgba(36, 28, 23, 0.18);
        }

        .fondue-reassurance {
          width: min(100%, 920px);
          margin: 24px auto 0;
          padding: 0 22px 20px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }

        .fondue-reassurance div {
          text-align: center;
          color: rgba(24, 18, 14, 0.96);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
          text-shadow: none;
        }

        .fondue-reassurance strong {
          font-size: 0.82rem;
          font-weight: 900;
          color: rgba(24, 18, 14, 0.95);
        }

        .fondue-reassurance small {
          font-size: 0.76rem;
          font-weight: 600;
          color: rgba(24, 18, 14, 0.76);
        }

        .fondue-list-screen,
        .fondue-detail-screen,
        .fondue-custom-screen {
          width: min(100%, 620px);
          margin: 0 auto;
          min-height: 100vh;
          border: none;
          border-radius: 0;
          overflow-x: hidden;
          box-shadow: none;
          position: relative;
        }

        .fondue-list-screen {
          padding-bottom: 18px;
        }

        .fondue-list-screen::before {
          content: "";
          position: absolute;
          inset: 0;
          background: rgba(251, 248, 241, 0.35);
          z-index: 0;
          pointer-events: none;
        }

        .fondue-detail-screen {
          background: #fbf8f1;
          padding-bottom: 104px;
        }

        .fondue-custom-screen {
          width: 100%;
          padding-bottom: 104px;
          background-repeat: no-repeat;
        }

        .fondue-custom-screen::before {
          content: "";
          position: absolute;
          inset: 0;
          background: rgba(251, 248, 241, 0.15);
          z-index: 0;
        }

        .fondue-list-screen > *,
        .fondue-custom-screen > * {
          position: relative;
          z-index: 1;
        }

        .fondue-list-topbar,
        .fondue-detail-topbar {
          height: 64px;
          display: grid;
          grid-template-columns: 54px 1fr 54px;
          align-items: center;
          border-bottom: 1px solid rgba(168, 138, 76, 0.08);
          background: #fbf8f1;
        }

        .fondue-list-screen .fondue-list-topbar,
        .fondue-custom-screen .fondue-detail-topbar {
          background: transparent;
          border-bottom: none;
        }

        .fondue-list-back,
        .fondue-detail-back,
        .fondue-detail-heart {
          border: none;
          background: transparent;
          font-size: 1.8rem;
          cursor: pointer;
          color: #241c17;
        }

        .fondue-detail-heart {
          font-size: 1.6rem;
          color: #7b4b2c;
        }

        .fondue-list-title,
        .fondue-detail-title {
          text-align: center;
          color: #b08a45;
          font-weight: 900;
          letter-spacing: 0.05em;
          font-size: 0.95rem;
          text-transform: uppercase;
        }

        .fondue-list-intro,
        .fondue-custom-intro {
          display: grid;
          grid-template-columns: 54px 1fr;
          align-items: center;
          gap: 14px;
          padding: 18px 26px 18px;
        }

        .fondue-list-icon {
          width: 46px;
          height: 46px;
          border-radius: 999px;
          border: 1px solid rgba(176, 138, 69, 0.22);
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(247, 241, 231, 0.72);
        }

        .fondue-list-icon .fondue-svg {
          width: 24px;
          height: 24px;
        }

        .fondue-list-intro-copy {
          text-align: left;
        }

        .fondue-list-intro h2,
        .fondue-list-intro p,
        .fondue-custom-intro h2,
        .fondue-custom-intro p,
        .fondue-detail-intro-card h2,
        .fondue-detail-intro-card p,
        .fondue-detail-panel h3,
        .fondue-ratio-head,
        .fondue-quantity-row,
        .fondue-custom-product strong,
        .fondue-custom-summary strong {
          font-family: "Playfair Display", "Times New Roman", serif;
        }

        .fondue-list-intro h2,
        .fondue-custom-intro h2 {
          font-size: 1.62rem;
          line-height: 1.08;
          font-weight: 700;
          letter-spacing: 0.01em;
          margin: 0 0 7px;
          color: #241c17;
        }

        .fondue-list-intro p,
        .fondue-custom-intro p {
          margin: 0;
          max-width: 390px;
          font-size: 0.98rem;
          line-height: 1.42;
          font-weight: 500;
          color: rgba(36, 28, 23, 0.72);
        }

        .fondue-filter-row {
          display: flex;
          gap: 10px;
          overflow-x: auto;
          padding: 0 26px 18px;
          scrollbar-width: none;
        }

        .fondue-filter-row::-webkit-scrollbar {
          display: none;
        }

        .fondue-filter {
          border: 1px solid rgba(168, 138, 76, 0.1);
          background: rgba(247, 241, 231, 0.92);
          color: rgba(36, 28, 23, 0.78);
          border-radius: 999px;
          padding: 11px 18px;
          font-weight: 700;
          white-space: nowrap;
          cursor: pointer;
          box-shadow: none;
        }

        .fondue-filter.active {
          background: #b08a45;
          color: #fff;
          border-color: #b08a45;
        }

        .fondue-list {
          display: grid;
          gap: 12px;
          padding: 0 26px 14px;
        }

        .fondue-list-card {
          min-height: 118px;

          background: rgba(255, 250, 242, 0.15); /* ⬅️ plus transparent */

          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);

          border: 1px solid rgba(168, 138, 76, 0.12);
          border-radius: 14px;

          display: grid;
          grid-template-columns: 124px 1fr 34px;
          align-items: stretch;

          overflow: hidden;
          padding: 0;
          text-align: left;
          cursor: pointer;

          box-shadow: 0 1px 0 rgba(255, 255, 255, 0.6) inset,
            0 12px 28px rgba(0, 0, 0, 0.12);
        }

        .fondue-list-image {
          position: relative;
          overflow: hidden;
          border-radius: 14px 0 0 14px;
        }

        .fondue-list-image img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          object-position: center;
        }

        .fondue-flag,
        .fondue-detail-badge {
          position: absolute;
          border-radius: 999px;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 900;
          box-shadow: 0 6px 14px rgba(0, 0, 0, 0.16);
        }

        .fondue-flag {
          top: 10px;
          left: 10px;
          width: 26px;
          height: 26px;
          font-size: 0.8rem;
        }

        .fondue-list-copy {
          padding: 16px 14px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 10px;
        }

        .fondue-list-copy strong {
          font-family: "Playfair Display", serif;
          font-size: 1.08rem;
          line-height: 1.18;
          color: #241c17;
        }

        .fondue-list-copy small {
          font-size: 0.86rem;
          line-height: 1.35;
          color: rgba(36, 28, 23, 0.72);
        }

        .fondue-detail-image {
          position: relative;
          height: 245px;
          margin: 10px 22px 0;
          border-radius: 14px 14px 0 0;
          overflow: hidden;
        }

        .fondue-detail-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }

        .fondue-detail-badge {
          top: 18px;
          left: 18px;
          width: 34px;
          height: 34px;
          font-size: 0.82rem;
        }

        .fondue-detail-content {
          margin: 0 22px;
          display: grid;
          gap: 8px;
        }

        .fondue-detail-intro-card,
        .fondue-detail-panel {
          background: rgba(247, 241, 231, 0.96);
          border: 1px solid rgba(168, 138, 76, 0.045);
          box-shadow: 0 1px 0 rgba(255, 255, 255, 0.32) inset;
        }

        .fondue-detail-intro-card {
          padding: 14px 16px 13px;
          border-radius: 0 0 14px 14px;
        }

        .fondue-detail-intro-card h2 {
          font-size: 1.5rem;
          line-height: 1.08;
          font-weight: 700;
          margin: 0 0 6px;
          color: #241c17;
        }

        .fondue-detail-intro-card p {
          margin: 0;
          font-size: 0.94rem;
          line-height: 1.34;
          font-weight: 500;
          color: rgba(36, 28, 23, 0.72);
        }

        .fondue-detail-panel {
          border-radius: 14px;
          padding: 13px 16px;
        }

        .fondue-detail-panel h3 {
          margin: 0 0 10px;
          font-size: 0.82rem;
          letter-spacing: 0.05em;
          color: #a47c36;
          font-weight: 700;
        }

        .fondue-ratio-block {
          margin-bottom: 10px;
        }

        .fondue-ratio-block:last-child {
          margin-bottom: 0;
        }

        .fondue-ratio-head {
          display: flex;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 6px;
          font-size: 0.95rem;
          font-weight: 500;
          color: #241c17;
        }

        .fondue-ratio-track {
          height: 1px;
          background: rgba(176, 138, 69, 0.18);
          border-radius: 999px;
          overflow: hidden;
        }

        .fondue-ratio-track span {
          display: block;
          height: 100%;
          background: #c08f34;
          border-radius: inherit;
        }

        .fondue-quantity-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          margin-top: 8px;
          font-size: 0.95rem;
          font-weight: 500;
          color: #241c17;
        }

        .fondue-quantity-row:first-of-type {
          margin-top: 0;
        }

        .fondue-people-control {
          display: grid;
          grid-template-columns: 34px 32px 34px;
          align-items: center;
          border: 1px solid rgba(176, 138, 69, 0.2);
          border-radius: 999px;
          overflow: hidden;
          background: rgba(255, 250, 242, 0.62);
        }

        .fondue-people-control button {
          border: none;
          background: transparent;
          color: #b08a45;
          font-size: 1.18rem;
          cursor: pointer;
          height: 30px;
        }

        .fondue-people-control strong {
          text-align: center;
          font-size: 0.9rem;
        }

        .fondue-added-feedback {
          text-align: center;
          font-size: 0.86rem;
          color: #8c6a2f;
          font-weight: 800;
          padding: 2px 0;
        }

        .fondue-custom-layout {
          padding: 0 22px 18px;
          display: grid;
          gap: 14px;
          max-width: 100%;
          overflow-x: hidden;
        }

        .fondue-custom-panel {
          border-radius: 18px;
          background: rgba(255, 250, 242, 0.45);
          backdrop-filter: blur(6px);
          border: 1px solid rgba(168, 138, 76, 0.06);
          padding: 14px;
          box-shadow: 0 1px 0 rgba(255, 255, 255, 0.38) inset;
          max-width: 100%;
          overflow-x: hidden;
        }

        .fondue-custom-panel h3 {
          margin: 0 0 12px;
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 0.88rem;
          letter-spacing: 0.06em;
          color: #a47c36;
          font-weight: 700;
        }

        .fondue-custom-categories {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 9px;
          overflow: visible;
          padding-bottom: 12px;
        }

        .fondue-custom-category {
          border: 1px solid rgba(176, 138, 69, 0.14);
          background: rgba(255, 250, 242, 0.72);
          color: rgba(36, 28, 23, 0.76);
          border-radius: 999px;
          padding: 9px 13px;
          font-size: 0.82rem;
          font-weight: 800;
          white-space: normal;
          min-height: 42px;
          text-align: center;
          cursor: pointer;
        }

        .fondue-custom-category.active {
          background: #b08a45;
          border-color: #b08a45;
          color: #fff;
        }

        .fondue-custom-search {
          width: 100%;
          height: 42px;
          border: 1px solid rgba(176, 138, 69, 0.16);
          border-radius: 14px;
          background: rgba(255, 250, 242, 0.78);
          padding: 0 14px;
          font-size: 0.9rem;
          color: #241c17;
          outline: none;
        }

        .fondue-custom-products-list {
          display: grid;
          gap: 10px;
          max-height: 360px;
          overflow-y: auto;
          padding-right: 3px;
        }

        .fondue-custom-product {
          min-height: 72px;
          border: 1px solid rgba(168, 138, 76, 0.07);
          border-radius: 16px;
          background: rgba(255, 250, 242, 0.72);
          box-shadow: 0 8px 18px rgba(84, 66, 38, 0.035);
          padding: 13px 14px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 12px;
          text-align: left;
          cursor: pointer;
        }

        .fondue-custom-product span {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .fondue-custom-product strong {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 1.02rem;
          line-height: 1.15;
          color: #241c17;
          overflow-wrap: anywhere;
        }

        .fondue-custom-product small,
        .fondue-custom-line small,
        .fondue-custom-placeholder,
        .fondue-custom-empty {
          font-size: 0.82rem;
          line-height: 1.35;
          color: rgba(36, 28, 23, 0.66);
          overflow-wrap: anywhere;
        }

        .fondue-custom-product em {
          font-style: normal;
          min-width: 34px;
          height: 34px;
          border-radius: 999px;
          background: #b08a45;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 12px;
          font-size: 0.78rem;
          font-weight: 900;
        }

        .fondue-custom-product.selected {
          opacity: 0.7;
        }

        .fondue-custom-product.selected em {
          background: rgba(176, 138, 69, 0.52);
        }

        .fondue-custom-lines {
          display: grid;
          gap: 10px;
        }

        .fondue-custom-line {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 12px;
          align-items: center;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(168, 138, 76, 0.1);
        }

        .fondue-custom-line:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .fondue-custom-line > div:first-child {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .fondue-custom-remove {
          width: 30px;
          height: 30px;
          border: none;
          border-radius: 999px;
          background: rgba(176, 138, 69, 0.12);
          color: #7b4b2c;
          font-size: 1.1rem;
          font-weight: 800;
          cursor: pointer;
        }

        .fondue-validate-caquelon {
          width: 100%;
          margin-top: 14px;
          border: none;
          border-radius: 14px;
          background: linear-gradient(135deg, #b8892e 0%, #a87925 100%);
          color: #fff;
          padding: 13px 16px;
          font-weight: 900;
          cursor: pointer;
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 1rem;
        }

        .fondue-ratio-presets {
          margin-top: 14px;
          padding-top: 12px;
          border-top: 1px solid rgba(168, 138, 76, 0.12);
        }

        .fondue-ratio-header {
          text-align: center;
          margin-bottom: 16px;
        }

        .fondue-ratio-mini-icon {
          width: 42px;
          height: 42px;
          margin: 0 auto 10px;
          border-radius: 999px;
          border: 1px solid rgba(176, 138, 69, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .fondue-ratio-mini-icon .fondue-svg {
          width: 22px;
          height: 22px;
        }

        .fondue-ratio-header h3 {
          margin: 0;
          font-size: 1.05rem;
          color: #241c17;
        }

        .fondue-ratio-header strong {
          display: block;
          margin-top: 4px;
          color: #b08a45;
          font-size: 0.86rem;
          letter-spacing: 0.05em;
        }

        .fondue-ratio-header small {
          display: block;
          margin-top: 6px;
          color: rgba(36, 28, 23, 0.62);
        }

        .fondue-ratio-card-grid {
          display: grid;
          gap: 12px;
        }

        .fondue-ratio-visual-card {
          position: relative;
          border: 1px solid rgba(176, 138, 69, 0.16);
          background: rgba(255, 250, 242, 0.7);
          border-radius: 18px;
          padding: 14px 12px 16px;
          cursor: pointer;
          text-align: center;
        }

        .fondue-ratio-visual-card.active {
          border-color: #b08a45;
          box-shadow: 0 10px 24px rgba(176, 138, 69, 0.12);
        }

        .fondue-ratio-check {
          position: absolute;
          top: 12px;
          left: 12px;
          width: 26px;
          height: 26px;
          border-radius: 999px;
          border: 1px solid rgba(176, 138, 69, 0.28);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          background: transparent;
          font-weight: 900;
        }

        .fondue-ratio-visual-card.active .fondue-ratio-check {
          background: #b08a45;
        }

        .fondue-ratio-composition-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
          gap: 12px;
          align-items: start;
          margin-top: 12px;
        }

        .fondue-ratio-composition-item {
          display: grid;
          gap: 6px;
          text-align: center;
        }

        .fondue-ratio-composition-item strong {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 1.05rem;
          color: #241c17;
          background: rgba(176, 138, 69, 0.16);
          border-radius: 10px;
          padding: 7px 8px;
          justify-self: center;
        }

        .fondue-ratio-composition-item small {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 0.9rem;
          font-weight: 700;
          line-height: 1.18;
          color: rgba(36, 28, 23, 0.82);
          overflow-wrap: anywhere;
        }

        .fondue-ratio-tip {
          margin-top: 14px;
          padding: 12px 14px;
          border-radius: 14px;
          background: rgba(255, 250, 242, 0.76);
          color: rgba(36, 28, 23, 0.7);
          font-size: 0.86rem;
          line-height: 1.45;
        }

        .fondue-ratio-total {
          margin-top: 14px;
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: center;
          color: rgba(36, 28, 23, 0.68);
          font-weight: 700;
        }

        .fondue-ratio-total.valid strong {
          color: #b08a45;
        }

        .fondue-sticky-cart {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          max-width: 620px;
          margin: 0 auto;
          background: linear-gradient(135deg, #b8892e 0%, #a87925 100%);
          color: #fff;
          border-radius: 12px 12px 0 0;
          padding: 14px 20px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 16px;
          box-shadow: 0 -10px 30px rgba(84, 66, 38, 0.2);
          z-index: 20;
        }

        .fondue-sticky-cart strong {
          display: block;
          font-size: 1.35rem;
          line-height: 1;
        }

        .fondue-sticky-cart small {
          display: block;
          margin-top: 5px;
          opacity: 0.9;
          max-width: 210px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fondue-sticky-cart button {
          border: none;
          border-radius: 13px;
          background: rgba(255, 255, 255, 0.26);
          color: #fff;
          padding: 12px 16px;
          font-weight: 900;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          line-height: 1.15;
        }

        .fondue-sticky-cart button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .fondue-sticky-cart button small {
          margin-top: 4px;
          font-weight: 600;
        }

        @media (max-width: 520px) {
          .fondue-entry-cards {
            width: 84%;
            margin-top: 24px;
          }

          .fondue-list-screen,
          .fondue-detail-screen,
          .fondue-custom-screen {
            width: 100%;
          }

          .fondue-list-intro,
          .fondue-custom-intro {
            grid-template-columns: 46px 1fr;
            gap: 12px;
            padding: 16px 20px;
          }

          .fondue-list-icon {
            width: 42px;
            height: 42px;
          }

          .fondue-list-intro h2,
          .fondue-custom-intro h2 {
            font-size: 1.38rem;
          }

          .fondue-list-card {
            grid-template-columns: 122px 1fr 30px;
          }

          .fondue-detail-image {
            margin: 8px 18px 0;
            height: 230px;
          }

          .fondue-detail-content {
            margin: 0 18px;
            gap: 7px;
          }

          .fondue-detail-screen,
          .fondue-custom-screen {
            padding-bottom: 96px;
          }

          .fondue-custom-layout {
            padding: 0 18px 16px;
          }

          .fondue-custom-panel {
            margin: 0;
            max-width: 100%;
            overflow: hidden;
          }

          .fondue-sticky-cart {
            padding: 13px 16px;
            gap: 12px;
          }

          .fondue-sticky-cart strong {
            font-size: 1.15rem;
          }

          .fondue-sticky-cart button {
            padding: 11px 12px;
            font-size: 0.78rem;
          }
        }
      `}</style>
    </main>
  );
}